import React from 'react';
import { string } from 'prop-types';
import { Box, Image, Heading, Text, Center, Button, useDisclosure } from '@chakra-ui/react';
import { useImaginationRoom } from '@invitato/imagination-room';

import useLang from '@/hooks/useLang';
import WithAnimation from '@/components/Common/WithAnimation';
import ImageHover from '@/components/Common/ImageHover';

import { BUTTON_PADDING_PROPS } from '@/constants/colors';
import { BRIDE_HASHTAG, THE_BRIDE } from '@/constants';
import {
  ASSETS_FLOWER_1,
  ASSETS_FLOWER_2,
  ASSETS_BUTTERFLY,
  ASSETS_KAWUNG_LOOPING,
  IMG_GALLERY_1,
  IMG_GALLERY_2,
  IMG_GALLERY_3,
} from '@/constants/assets';
import { TEXT_PROPS } from '@/constants/default-props';

import txt from './locales';
import Content from './Content';

function PhotoSection() {
  const { onGetFinalContent } = useImaginationRoom();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const lang = useLang();

  return (
    <Box px={{ '3sm': '60px', '2sm': '16px', sm: '16px' }}>
      <Box
        position="relative"
        marginTop={{ '3sm': '80px', '2sm': '50px', '1sm': '50px', sm: '60px' }}
        height="100%"
      >
        {/* Title & Desc Section */}
        <Box textAlign="center">
          <WithAnimation>
            <Heading {...TEXT_PROPS.heading} textTransform="none" marginTop="16px">
              a Portrait of
            </Heading>
          </WithAnimation>
          <WithAnimation>
            <Text
              {...TEXT_PROPS.body}
              color="secondaryColorText"
              fontSize={BRIDE_HASHTAG ? '20px' : '32px'}
              fontWeight="bold"
            >
              {BRIDE_HASHTAG || THE_BRIDE}
            </Text>
          </WithAnimation>
        </Box>
        {/* GALLERY COVER */}
        <Box pos="relative" width="100%" minHeight="60vh">
          {/* ASSETS BATIK KAWUNG */}
          <Box
            pos="absolute"
            top="-50%"
            left="40%"
            width="12%"
            height="100%"
            transform="rotate(90deg)"
            bgRepeat="repeat-y"
            bgImage={ASSETS_KAWUNG_LOOPING}
            bgSize="contain"
            bgPos="top center"
            zIndex="1"
          />

          {/* ASSETS BUTTERFLY */}
          <Box
            className="animation-up-and-down"
            position="absolute"
            top="-70px"
            left="0px"
            zIndex="3"
          >
            <WithAnimation bottom delay={1000}>
              <Image src={ASSETS_BUTTERFLY} maxW="50px" />
            </WithAnimation>
          </Box>

          {/* ASSETS FLOWERS */}
          <Box position="absolute" top="20px" left={{ '3sm': '-170px', base: '-140px' }} zIndex="1">
            <WithAnimation left>
              <Image
                src={ASSETS_FLOWER_1}
                width={{ '3sm': '200px', '2sm': '180px', sm: '180px' }}
                transform="rotate(16deg)"
              />
            </WithAnimation>
          </Box>
          <Box
            className="animation__flower"
            position="absolute"
            top="180px"
            left="-160px"
            zIndex="3"
          >
            <WithAnimation left>
              <Image
                src={ASSETS_FLOWER_2}
                width={{ '3sm': '220px', '2sm': '210px', sm: '210px' }}
                transform="rotate(50deg)"
              />
            </WithAnimation>
          </Box>
          <Box
            position="absolute"
            top="20px"
            right={{ '3sm': '-140px', base: '-130px' }}
            zIndex="3"
            transform="rotate(290deg)"
          >
            <WithAnimation right>
              <Image
                src={ASSETS_FLOWER_2}
                width={{ '3sm': '220px', '2sm': '210px', sm: '210px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>
          <Box
            className="animation__flower"
            position="absolute"
            top="200px"
            right={{ '3sm': '-160px', base: '-130px' }}
            zIndex="3"
          >
            <WithAnimation right>
              <Image
                src={ASSETS_FLOWER_1}
                width={{ '3sm': '200px', '2sm': '180px', sm: '180px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>

          {/* Gallery Cover */}
          <Center flexDir="column" gap={2} marginTop="80px" pos="relative" zIndex="2">
            <Box width="85%">
              <WithAnimation type="zoom">
                <ImageHover
                  images={[onGetFinalContent('IMG_GALLERY_1', IMG_GALLERY_1)]}
                  imageProps={{
                    height: '200px',
                    width: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    borderRadius: '4px',
                  }}
                />
              </WithAnimation>
            </Box>
            <Center flexDir="row" width="85%" gap={2}>
              <Box width="50%">
                <WithAnimation type="zoom" delay={500}>
                  <ImageHover
                    images={[onGetFinalContent('IMG_GALLERY_2', IMG_GALLERY_2)]}
                    imageProps={{
                      height: '200px',
                      width: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      borderRadius: '4px',
                    }}
                  />
                </WithAnimation>
              </Box>
              <Box width="50%">
                <WithAnimation type="zoom" delay={1000}>
                  <ImageHover
                    images={[onGetFinalContent('IMG_GALLERY_3', IMG_GALLERY_3)]}
                    imageProps={{
                      height: '200px',
                      width: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      borderRadius: '6px',
                    }}
                  />
                </WithAnimation>
              </Box>
            </Center>
            <WithAnimation>
              <Button
                onClick={() => onOpen()}
                margin="24px auto 48px auto"
                {...BUTTON_PADDING_PROPS}
              >
                {txt.btnGallery[lang]}
              </Button>
            </WithAnimation>
            {/* Content gallery */}
            <Content isOpen={isOpen} onClose={onClose} />
          </Center>
        </Box>
      </Box>
    </Box>
  );
}

PhotoSection.propTypes = {
  lang: string.isRequired,
};

export default React.memo(PhotoSection);
